import { AlertTypes } from "@/components/SoAlert.vue"
import { SoDataTableHeader } from "@/types/components/SoDataTable"
import Papa from "papaparse"

export interface UploadResult<T = Record<string, string>> {
  totalCount: number
  addedCount: number
  updatedCount: number
  errorCount: number
  errors: T[]
  fileError?: string | null
}

export const parseFile = <T = Record<string, string>>(
  text: string,
  headers: SoDataTableHeader[],
): { rows: T[]; error: null } | { rows: null; error: string } => {
  const cleaned = text.replace(/^\uFEFF/, "")
  const result = Papa.parse(cleaned, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim().toLowerCase(),
  })
  const data = result.data as Record<string, string>[]
  const { errors, meta } = result

  if (errors.length > 0 && data.length === 0) {
    return { rows: null, error: "the CSV could not be parsed." }
  }

  const fileHeaders = new Set(meta.fields ?? [])
  const missingColumns = headers
    .map((header) => header.title)
    .filter((col) => !fileHeaders.has(col))

  if (missingColumns.length > 0) {
    return {
      rows: null,
      error:
        "the CSV is missing required columns. Download the template to see the expected format.",
    }
  }

  if (data.length === 0) {
    return { rows: null, error: "the CSV file contains no data." }
  }

  const rows = data
    .filter((raw) => Object.values(raw).some((v) => v && v.trim() !== ""))
    .map((raw) =>
      headers.reduce(
        (prev, header) => ({
          ...prev,
          [header.key]: (raw[header.title] ?? "").trim(),
        }),
        {} as T,
      ),
    )

  return { rows, error: null }
}

export const formatError = <T = Record<string, string>>(
  hasNetworkError = false,
  uploadResult: UploadResult<T> | null = null,
): { text: string; type: AlertTypes } | null => {
  if (hasNetworkError) {
    return { text: "Upload failed. Please try again.", type: "error" }
  }

  console.log(uploadResult)

  if (!uploadResult) return null

  if (uploadResult.fileError) {
    return {
      text: `The file could not be processed: ${uploadResult.fileError}`,
      type: "warning",
    }
  }
  if (uploadResult.errorCount === 0) {
    return {
      text: `Upload complete. ${uploadResult.addedCount} rows added, ${uploadResult.updatedCount} rows updated.`,
      type: "success",
    }
  }
  return {
    text: `Upload complete with ${uploadResult.errorCount} row errors. ${uploadResult.addedCount} rows added, ${uploadResult.updatedCount} rows updated. Download the errors CSV for details.`,
    type: "warning",
  }
}
